import { Link, router } from '@inertiajs/react';
import { format } from 'date-fns';
import { ArrowLeft, Code, Copy, Download, Edit, Eye, File, FileText, Trash2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';

import { MarkdownViewer } from '@/components/markdown-viewer';
import { OpenAPIViewer } from '@/components/openapi-viewer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// Add missing imports at the top
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AppLayout from '@/layouts/app-layout';
import { detectFileType } from '@/utils/file-utils';

interface ShareFile {
  id: string;
  filename: string;
  content_type: string;
  byte_size: number;
  created_at: string;
  url: string;
}

interface ShareShowProps {
  share: {
    id: number;
    title: string;
    description: string;
    slug: string;
    expires_at: string | null;
    view_count: number;
    created_at: string;
    public_url: string;
    files: ShareFile[];
  };
}

export default function ShareShow({ share }: ShareShowProps) {
  const [selectedFile, setSelectedFile] = useState<ShareFile | null>(share.files[0] || null);
  const [fileContent, setFileContent] = useState<string>('');
  const [copiedLink, setCopiedLink] = useState(false);
  
  // Auto-load first file content on mount
  useEffect(() => {
    if (share.files.length > 0) {
      void loadFileContent(share.files[0]);
    }
  }, []);

  const handleCopyLink = () => {
    void navigator.clipboard.writeText(share.public_url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this share?')) {
      router.delete(`/shares/${share.id}`);
    }
  };

  const loadFileContent = async (file: ShareFile) => {
    setSelectedFile(file);
    
    // Fetch file content
    try {
      const response = await fetch(file.url);
      const content = await response.text();
      setFileContent(content);
    } catch (error) {
      console.error('Error loading file:', error);
      setFileContent('Error loading file content');
    }
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'md' || ext === 'txt') return <FileText className="w-5 h-5" />;
    if (['json', 'yaml', 'yml'].includes(ext ?? '')) return <Code className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderFileContent = () => {
    if (!selectedFile || !fileContent) return null;

    const fileType = detectFileType(selectedFile.filename, fileContent);
    
    switch (fileType) {
      case 'markdown':
        return <MarkdownViewer content={fileContent} />;
      
      case 'openapi':
        return <OpenAPIViewer spec={fileContent} />;
      
      default:
        // Default to code view
        return (
          <Card>
            <CardContent className="p-6">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <code>{fileContent}</code>
              </pre>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-6">
          <Link href="/shares" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shares
          </Link>
          
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">{share.title}</h1>
              {share.description && (
                <p className="text-gray-600 dark:text-gray-400">
                  {share.description}
                </p>
              )}
              
              {/* Meta information inline */}
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                <span>
                  Created {format(new Date(share.created_at), 'PPP')}
                </span>
                <span className="flex items-center">
                  <Eye className="w-4 h-4 mr-1" />
                  {share.view_count} views
                </span>
                {share.expires_at && (
                  <span className="text-orange-600 dark:text-orange-400">
                    Expires {format(new Date(share.expires_at), 'PPP')}
                  </span>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center space-x-2">
              <Button onClick={handleCopyLink} variant="outline" size="sm">
                <Copy className="w-4 h-4 mr-1" />
                {copiedLink ? 'Copied!' : 'Copy Link'}
              </Button>
              <Link href={`/shares/${share.id}/edit`}>
                <Button variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              </Link>
              <Button variant="outline" size="sm" onClick={handleDelete}>
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* File selector - horizontal scrollable for multiple files */}
        {share.files.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">Files:</span>
              {share.files.map((file) => (
                <button
                  key={file.id}
                  onClick={() => void loadFileContent(file)}
                  className={`inline-flex items-center px-4 py-2 rounded-lg border whitespace-nowrap transition-colors ${
                    selectedFile?.id === file.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {getFileIcon(file.filename)}
                  <span className="ml-2 text-sm font-medium">{file.filename}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    ({formatFileSize(file.byte_size)})
                  </span>
                </button>
              ))}
              {selectedFile && (
                <a
                  href={selectedFile.url}
                  download={selectedFile.filename}
                  className="inline-flex items-center px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </a>
              )}
            </div>
          </div>
        )}

        {/* Main content viewer - full width */}
        {selectedFile ? (
          <div className="w-full">
            {renderFileContent()}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <CardContent>
              <File className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">
                {share.files.length > 0 
                  ? 'Select a file to view its contents'
                  : 'No files attached to this share'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
